import { createVNode, render, ref } from 'vue'
import type { App } from 'vue'

export enum AuthDialogType {
  Login = 'login',
  Register = 'register'
}

export interface AuthDialogConfig {
  /** 显示类型：登录或注册 */
  type?: AuthDialogType
  /** 登录/注册成功回调 */
  onSuccess?: (userData: any) => void
  /** 取消回调 */
  onCancel?: () => void
  /** 取消时是否返回首页 */
  redirectOnCancel?: boolean
}

export interface UserData {
  id: number | string
  userId: number | string
  username: string
  email?: string
  avatar?: string
  registeredAt?: string
  accessToken?: string
}

/**
 * 登录弹窗工具类
 */
class AuthDialog {
  private app: App | null = null
  private callbacks: {
    onSuccess?: (userData: any) => void
    onCancel?: () => void
    redirectOnCancel?: boolean
  } = {}
  private currentVNode: any = null
  private currentContainer: HTMLElement | null = null

  // 响应式的显示状态
  private _visible = ref(false)
  private _currentType = ref<AuthDialogType | null>(null)

  /**
   * 设置Vue应用实例（用于访问全局配置和插件）
   */
  setApp(app: App) {
    this.app = app
    return this
  }

  /**
   * 显示登录/注册弹窗
   */
  async show(config: AuthDialogConfig = {}) {
    const { type = AuthDialogType.Login, onSuccess, onCancel, redirectOnCancel = true } = config

    // 保存回调函数
    this.callbacks = { onSuccess, onCancel, redirectOnCancel }

    // 如果已有实例，先关闭
    this.hide()

    // 根据类型选择组件
    const component =
      type === AuthDialogType.Register
        ? (await import('@/components/auth/RegisterDialog.vue')).default
        : (await import('@/components/auth/LoginDialog.vue')).default

    // 创建容器
    this.currentContainer = document.createElement('div')
    document.body.appendChild(this.currentContainer)

    // 创建 VNode
    this.currentVNode = createVNode(component, {
      modelValue: true,
      onSuccess: (userData: UserData) => {
        this.handleSuccess(userData)
      },
      onCancel: () => {
        this.handleCancel()
      },
      onSwitchToLogin: () => {
        this.show({ type: AuthDialogType.Login, onSuccess, onCancel, redirectOnCancel })
      },
      onSwitchToRegister: () => {
        this.show({ type: AuthDialogType.Register, onSuccess, onCancel, redirectOnCancel })
      }
    })

    // 绑定应用上下文（如果有的话）
    if (this.app) {
      this.currentVNode.appContext = this.app._context
    }

    // 渲染组件
    render(this.currentVNode, this.currentContainer)

    // 更新响应式状态
    this._visible.value = true
    this._currentType.value = type

    return this
  }

  /**
   * 显示登录页面
   */
  login(onSuccess?: (userData: UserData) => void) {
    return this.show({
      type: AuthDialogType.Login,
      onSuccess
    })
  }

  /**
   * 显示注册页面
   */
  register(onSuccess?: (userData: UserData) => void) {
    return this.show({
      type: AuthDialogType.Register,
      onSuccess
    })
  }

  /**
   * 隐藏登录弹窗
   */
  hide() {
    if (this.currentVNode && this.currentContainer) {
      // 卸载 VNode
      render(null, this.currentContainer)
      // 移除容器
      if (this.currentContainer.parentNode) {
        this.currentContainer.parentNode.removeChild(this.currentContainer)
      }
      this.currentVNode = null
      this.currentContainer = null
    }

    // 更新响应式状态
    this._visible.value = false
    this._currentType.value = null
  }

  /**
   * 获取响应式的显示状态
   */
  get visible() {
    return this._visible
  }

  /**
   * 获取响应式的当前弹窗类型
   */
  get currentType() {
    return this._currentType
  }

  /**
   * 登录/注册成功时调用
   */
  handleSuccess(userData: UserData) {
    console.log('Auth success:', userData)
    this.callbacks.onSuccess?.(userData)
    this.clearCallbacks()
    this.hide()
  }

  /**
   * 登录/注册取消时调用
   */
  handleCancel() {
    console.log('Auth cancelled')
    const redirectOnCancel = this.callbacks.redirectOnCancel
    this.callbacks.onCancel?.()
    this.clearCallbacks()
    this.hide()
    if (redirectOnCancel && location.pathname !== '/') {
      location.replace('/')
    }
  }

  /**
   * 清除回调函数
   */
  private clearCallbacks() {
    this.callbacks = {}
  }
}

// 创建单例实例
const authDialog = new AuthDialog()

export default authDialog

// 导出便捷方法
export const showLogin = (onSuccess?: (userData: UserData) => void) => {
  return authDialog.login(onSuccess)
}

export const showRegister = (onSuccess?: (userData: UserData) => void) => {
  return authDialog.register(onSuccess)
}
